import {
    Collisions,
    GameRandom,
    Inject,
    List,
    ModelEvents,
    Singleton,
} from '@denishussar/html5-game-engine';

import { BlockCardsModel } from '../../Controllers/Game/MiniGames/block_cards/Models/BlockCardsModel';
import { ExchangeModel } from '../../Controllers/Game/MiniGames/currency_exchange/Models/ExchangeModel';
import { ElectricityModel } from '../../Controllers/Game/MiniGames/electricity/Models/ElectricityModel';
import { LoanModel } from '../../Controllers/Game/MiniGames/loan_game/Models/LoanModel';
import { LockCellModel } from '../../Controllers/Game/MiniGames/lock_cell/Models/LockCellModel';
import { DinnerModel } from '../../Controllers/Game/MiniGames/dinner/Models/DinnerModel';
import { SendLetterModel } from '../../Controllers/Game/MiniGames/send_letter/Models/SendLetterModel';
import { PrintCardModel } from '../../Controllers/Game/MiniGames/print_card/Models/PrintCardModel';
import { BussinesTripModel } from '../../Controllers/Game/MiniGames/bussines_trip/Models/BussinesTripModel';
import { UnitModel } from './UnitModel';
import { GamePadModel } from './GamePadModel';
import { PlayerModel } from './PlayerModel';
import { RoomModel } from './RoomModel';
import { MiniGameModel } from './MiniGameModel';
import { TaskModel, TaskModelOptions } from './TaskModel';
import { AchievementModel, AchievementType } from './AchievementModel';
import isMobile from 'is-mobile';

export enum GameActionEnum {
    none = 0,
}

export enum DifficultyEnum {
    none = 'none',
    easy = 'easy',
    medium = 'medium',
    hard = 'hard',
}

export enum AppStateEnum {
    none = 0,
    menu = 1,
    game = 2,
    pause = 3,
    save = 4,
}

export enum GameStateEnum {
    none = 0,
    pause = 1,
    settings = 2,
    rules = 3,
    gameEnd = 4,
    achievements = 5,
    restart = 6,
    restartToMenu = 7,
    minimap = 8,
    playerName = 9,
    leaderboards = 10,
}

export enum MenuStateEnum {
    none = 0,
    menu = 1,
    selectDifficulty = 2,
    selectCharacter = 3,
    settings = 4,
    achievements = 5,
    leaderboards = 6,
    rules = 7,
    pause = 8,
}

export enum TooltipStateEnum {
    none = 0,
    task = 'task',
    bonus = 'bonus',
    achievement = 'achievement',
}

@Singleton
export class GameModel {
    public showMiniGame: boolean = false;
    public showMiniMap: boolean = false;
    public showPauseScreen: boolean = false;

    public isWoman: boolean = false;

    public referenceScreenNumberOfPages: number = 1;
    public referenceScreenCurrentPage: number = 1;

    public soundOnOff: boolean = true;
    public musicOnOff: boolean = true;

    public player: PlayerModel = null;
    public playerName: string;
    public playerGuid: string;
    public isFirstLaunch: boolean = true;
    public isFirstSession: boolean = true;

    public gamePad: GamePadModel = new GamePadModel();
    public currMiniGame: MiniGameModel;

    public unitList = new List<UnitModel>();
    public roomList: List<RoomModel> = new List<RoomModel>();

    public pos: [number, number] = [0, 0];
    public screenPos: [number, number] = [0, 0];
    public scale: number = 0.7;
    public action: [GameActionEnum, number, number] = [0, 0, 0];

    // Time
    public totalTime: number = 3 * 60;

    // Tasks
    public taskCount: number = 3;
    public taskOptionList: List<TaskModelOptions> =
        new List<TaskModelOptions>();
    public taskCurrentList: List<TaskModel> = new List<TaskModel>();

    // Achievement
    public achievementList: List<AchievementModel> =
        new List<AchievementModel>();
    public tooltipIcon: string = '';
    public collectAchievements = [];

    // Bonus
    public bonusCount: number = 3;
    public bonusGenerationTime: number = 0;

    public bonusDoubleScoreActive: boolean = false;
    public bonusHalfScoreActive: boolean = false;
    public activateLogoBonus: boolean = false;

    // Tooltip
    public tooltipText: string = '';
    public tooltipState: TooltipStateEnum = TooltipStateEnum.none;

    // Difficulty
    public difficulty: DifficultyEnum = DifficultyEnum.none;
    public difficultyConfig: any = {};

    // State
    public appState: AppStateEnum = AppStateEnum.none;
    public menuState: MenuStateEnum = MenuStateEnum.none;
    public gameState: GameStateEnum = GameStateEnum.none;

    constructor(
        @Inject private _$random: GameRandom,
        @Inject private _$modelEvents: ModelEvents,
        @Inject private _$collisions: Collisions,
        @Inject public ExchangeModel: ExchangeModel,
        @Inject public BlockCardsModel: BlockCardsModel,
        @Inject public ElectricityModel: ElectricityModel,
        @Inject public LoanModel: LoanModel,
        @Inject public LockCellModel: LockCellModel,
        @Inject public DinnerModel: DinnerModel,
        @Inject public SendLetterModel: SendLetterModel,
        @Inject public PrintCardModel: PrintCardModel,
        @Inject public BussinesTripModel: BussinesTripModel
    ) {
        this.reset();
    }

    public reset(): void {
        this.menuState = MenuStateEnum.none;
        this.gameState = GameStateEnum.none;

        this.showMiniGame = false;
        this.showPauseScreen = false;

        this.pos = [0, 0];
        this.screenPos = [0, 0];
        this.scale = isMobile() ? 0.75 : 0.52;
        this.action = [0, 0, 0];

        this.bonusGenerationTime = 5;
        this.referenceScreenCurrentPage = 1;

        if (!this.taskOptionList) {
            this.taskOptionList = new List<TaskModelOptions>();
        } else {
            this.taskOptionList.clear();
        }

        if (!this.taskCurrentList) {
            this.taskCurrentList = new List<TaskModel>();
        } else {
            this.taskCurrentList.clear();
        }

        if (!this.roomList) {
            this.roomList = new List<RoomModel>();
        } else {
            this.roomList.clear();
        }

        if (!this.unitList) {
            this.unitList = new List<UnitModel>();
        } else {
            this.unitList.forEach(o => {
                if (o.collision) {
                    this._$collisions.remove(o.collision);
                }
            });
            this.unitList.clear();
        }

        if (!this.achievementList) {
            this.achievementList = new List<AchievementModel>();
        } else {
            this.collectAchievements = [];
            this.achievementList.clear();
        }

        const achievementsType = Object.values(AchievementType).filter(
            value => typeof value === 'string'
        );

        for (let i = 0; i < 4; i++) {
            let achievement = new AchievementModel(i, achievementsType[i]);
            this.achievementList.add(achievement);
        }

        if (!this.player) {
            this.player = new PlayerModel();
        }

        this.player.id = '';
        // @ts-ignore
        this.player.name = name;
        this.player.time = this.totalTime;
        this.player.score = 0;

        this.addUnit(this.player);
    }

    public addUnit(playerModel: PlayerModel | null): UnitModel {
        let unit = new UnitModel(this.unitList.count());
        unit.pos = [2300, 650];
        this.unitList.add(unit);

        unit.player = playerModel;

        unit.collision = this._$collisions.createCircle(0, 0, 40);

        unit.visible = true;
        unit.name = playerModel.name;
        unit.guid = playerModel.guid;

        return unit;
    }

    public addRoom(pos: [number, number], key: string): RoomModel {
        let index = key.replace('room', '');
        let model = new RoomModel(parseInt(index));

        model.pos = pos;
        model.key = key;

        this.roomList.add(model);

        return model;
    }

    public addScore(score: number) {
        let player = this.player;
        if (player) {
            player.score = Math.max(0, Math.floor(player.score + score));
        }
    }
}
